import { useNavigate, useSearchParams } from "react-router-dom";
import useRoles from "../../features/Roles/hooks/useRoles";
import { useEffect, useState } from "react";
import { 
  Button, 
  Center, 
  Flex, 
  Heading, 
  VStack, 
  Box,
  Text,
  Spinner,
  TableContainer,
  Table as ChakraTable,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Badge,
  Image,
  Input,
  Select
} from "@chakra-ui/react";
import { FaPlus } from "react-icons/fa";
import { ROUTES } from "../../consts/routes";
import { OlaggModule, PermissionEnum } from "@olagg/db-types";
import Pagination from "@olagg/ui-kit/Pagination";
import useSeasons from "./hooks/useSeasons";
import { AdminSeason } from "@olagg/db-types";
import moment from "moment";
import { GiBirdClaw } from "react-icons/gi";
import { fromLocalDateTimeToUTC, toLocalTimezone } from '../../utils';

const Actions = ({ season, onSeasonDeleted, onSeasonCloned }: { season: AdminSeason; onSeasonDeleted: () => void; onSeasonCloned: () => void }) => {
  const { missingPermission } = useRoles();
  const { deleteSeason, cloneSeason } = useSeasons();
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  
  const handleViewSeason = () => navigate(`/seasons/${season.id}`);
  const handleViewChallenges = () => navigate(`/seasons/${season.id}/challenges`);
  const handleViewSeasonClawPrizes = () => navigate(`/seasons/${season.id}/claw-machine-prizes`);

  const handleCloneSeason = async () => {
    if (window.confirm('¿Estás seguro que deseas clonar esta temporada?')) {
      setIsCloning(true);
      try {
        await cloneSeason(season.id.toString());
        console.log("Temporada clonada");
        // Llamar al callback para refrescar las temporadas
        await onSeasonCloned();
      } catch (error) {
        console.error("Error clonando temporada:", error);
      } finally {
        setIsCloning(false);
      }
    }
  }

  const handleRemoveSeason = async () => {
    if (window.confirm('¿Estás seguro que deseas eliminar esta temporada?')) {
      setIsDeleting(true);
      try {
        await deleteSeason(season.id.toString());
        console.log("Temporada eliminada");
        // Llamar al callback para refrescar las temporadas
        await onSeasonDeleted();
      } catch (error) {
        console.error("Error eliminando temporada:", error);
      } finally {
        setIsDeleting(false);
      }
    }
  }

  const canEdit = !missingPermission(OlaggModule.Season, PermissionEnum.EDIT);
  const canDelete = !missingPermission(OlaggModule.Season, PermissionEnum.DELETE);

  return (
    <Box display="flex" gap="10px" flexWrap="wrap">
      <Button 
        isDisabled={!canEdit} 
        colorScheme="green"
        size="sm"
        onClick={handleViewSeason}
      >
        Ver
      </Button>
      <Button 
        isDisabled={!canEdit || isCloning || isDeleting} 
        colorScheme="blue"
        size="sm"
        onClick={handleCloneSeason}
        isLoading={isCloning}
        loadingText="Clonando..."
      >
        Clonar
      </Button>
      <Button 
        isDisabled={!canEdit} 
        colorScheme="purple"
        size="sm"
        onClick={handleViewChallenges}
      >
        Retos
      </Button>
      <Button 
        isDisabled={!canEdit} 
        colorScheme="green"
        size="sm"
        onClick={handleViewSeasonClawPrizes}
        gap={2}
      >
        Premios <GiBirdClaw />
      </Button>
      <Button 
        isDisabled={!canDelete || isCloning || isDeleting} 
        colorScheme="red"
        size="sm"
        onClick={handleRemoveSeason}
        isLoading={isDeleting}
        loadingText="Eliminando..."
      >
        Eliminar
      </Button>
    </Box>
  )
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active': return 'green'
    case 'upcoming': return 'blue'
    case 'finished': return 'gray'
    default: return 'gray'
  }
}

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'active': return 'Activa'
    case 'upcoming': return 'Próxima'
    case 'finished': return 'Finalizada'
    default: return status
  }
}

const SeasonsList = () => {
  const navigate = useNavigate();
  const { missingPermission } = useRoles();
  const [searchParams, setSearchParams] = useSearchParams()
  const { getSeasons, loading } = useSeasons();
  const [seasons, setSeasons] = useState<AdminSeason[]>([]);
  const [count, setCount] = useState(0);
  const [debouncedTitle, setDebouncedTitle] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [filter, setFilter] = useState({
    title: searchParams.get('title') || '',
    page: Number(searchParams.get('page') || 1),
    limit: Number(searchParams.get('limit') || 10),
    visible: searchParams.get('visible') ? searchParams.get('visible') === 'true' : undefined,
    status: searchParams.get('status') as 'upcoming' | 'active' | 'finished' | undefined
  })
  const { page, limit, title, visible, status } = filter

  const onFilter = ({
    name,
    value
  }: { name: string, value: any }) => {
    const parsedValue = (name === 'page' || name === 'limit') ? Number(value) : value;
    setFilter({ ...filter, [name]: parsedValue })

    if (value) searchParams.set(name, value)
    else searchParams.delete(name)
    setSearchParams(searchParams)
  }

  const onClearFilters = () => {
    setFilter({
      title: '',
      page: 1,
      limit: 10,
      visible: undefined,
      status: undefined
    });
    setSearchParams(new URLSearchParams());
  }

  const refreshSeasons = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const fetchSeasons = async () => {
    try {
      const response = await getSeasons({ 
        limit, 
        page, 
        title: debouncedTitle || undefined,
        visible,
        status
      });
      setSeasons(response.gameSeasons);
      setCount(response.meta.count);
    } catch (error) {
      console.error('Error fetching seasons:', error);
    }
  };

  // Debounce title filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTitle(title)
    }, 500)

    return () => clearTimeout(timer)
  }, [title])

  useEffect(() => {
    fetchSeasons();
  }, [page, limit, debouncedTitle, visible, status, refreshTrigger])

  useEffect(() => {
    const urlPage = searchParams.get('page');
    const urlLimit = searchParams.get('limit');
    const urlTitle = searchParams.get('title');
    const urlVisible = searchParams.get('visible');
    const urlStatus = searchParams.get('status');

    if (urlPage || urlLimit || urlTitle || urlVisible || urlStatus) {
      setFilter({
        title: urlTitle || '',
        page: Number(urlPage || 1),
        limit: Number(urlLimit || 10),
        visible: urlVisible ? urlVisible === 'true' : undefined,
        status: urlStatus as 'upcoming' | 'active' | 'finished' | undefined
      });
    }
  }, []);

  return (
    <Center w='full' pb={4}>
      <Box color={'white'} w='full'>
        <Heading textAlign="center" mb={6}>
          Temporadas
        </Heading>

        <Flex w='full' alignItems='center' gap='5'>
          <Button
            mt='2' mb='5'
            display='flex'
            variant="outlinedGradient"
            leftIcon={<FaPlus />}
            onClick={() => navigate(ROUTES.SEASONS.create)}
            isDisabled={missingPermission(OlaggModule.Season, PermissionEnum.CREATE)}
          >
            Agregar Temporada
          </Button>
        </Flex>

        {/* Inline filter */}
        <Box
          mb={6}
          p={4}
          bg="gray.800"
          borderRadius="md"
          border="1px"
          borderColor="gray.600"
          display="grid"
          gridTemplateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }}
          gap={4}
        >
          <Box>
            <Text color="white" fontSize="sm" mb={1}>
              Título
            </Text>
            <Input
              placeholder="Buscar por título..."
              value={title}
              onChange={(e) => onFilter({ name: 'title', value: e.target.value })}
              bg="gray.700"
              borderColor="gray.600"
              color="white"
              size="sm"
            />
          </Box>

          <Box>
            <Text color="white" fontSize="sm" mb={1}>
              Estado
            </Text>
            <Select
              value={status || ''}
              onChange={(e) => onFilter({ name: 'status', value: e.target.value || undefined })}
              bg="gray.700"
              borderColor="gray.600"
              color="white"
              size="sm"
            >
              <option value="">Todos los estados</option>
              <option value="upcoming">Próxima</option>
              <option value="active">Activa</option>
              <option value="finished">Finalizada</option>
            </Select>
          </Box>

          <Box>
            <Text color="white" fontSize="sm" mb={1}>
              Visibilidad
            </Text>
            <Select
              value={visible === undefined ? '' : visible.toString()}
              onChange={(e) => {
                const value = e.target.value
                onFilter({ 
                  name: 'visible', 
                  value: value === '' ? undefined : value === 'true'
                })
              }}
              bg="gray.700"
              borderColor="gray.600"
              color="white"
              size="sm"
            >
              <option value="">Todas</option>
              <option value="true">Visible</option>
              <option value="false">Oculta</option>
            </Select>
          </Box>

          <Box>
            <Text color="transparent" fontSize="sm" mb={1}>
              .
            </Text>
            <Button
              colorScheme="gray"
              variant="outline"
              onClick={onClearFilters}
              size="sm"
              w="full"
            >
              Limpiar filtros
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box height='300px' display='flex' justifyContent='center' alignItems='center' fontWeight='600' flexDirection='column'>
            <Spinner />
          </Box>
        ) : (
          seasons?.length > 0 ? (
            <Flex w={'full'} pb={4} justifyContent='center' flexDirection={'column'}>
              <Flex w='full' justifyContent='center' px={'20px'} pb={'15px'}>
                <TableContainer overflowX={'scroll'} overflowY={'scroll'} w="100%" maxH="60vh">
                  <ChakraTable
                    variant="striped"
                    size="sm"
                    colorScheme="olaggPurple"
                    fontSize="18px"
                    position='relative'
                    whiteSpace={'normal'}
                  >
                    <Thead>
                      <Tr position='sticky' bg='gray.800' top='0' zIndex='100'>
                        <Th py={'20px'} color="white" colSpan={2}>Título</Th>
                        <Th py={'20px'} color="white">Estado</Th>
                        <Th py={'20px'} color="white">Visible</Th>
                        <Th py={'20px'} color="white">Fecha Inicio</Th>
                        <Th py={'20px'} color="white">Fecha Fin</Th>
                        <Th py={'20px'} color="white">Duracion (dias)</Th>
                        <Th py={'20px'} color="white">Retos</Th>
                        <Th py={'20px'} color="white">Premios de la Garra</Th>
                        <Th py={'20px'} color="white">Acciones</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {seasons?.map((season, index) => (
                        <Tr key={index} color="white">
                          <Td py={'20px'}>
                            {season?.image ? (
                              <Image src={season.image} w="40px" h="40px" borderRadius="md" />
                            ) : (
                              <Box w="40px" h="40px" bg="gray.600" borderRadius="md" />
                            )}
                          </Td>
                          <Td py={'20px'}>{season?.title}</Td>
                          <Td py={'20px'}>
                            <Badge colorScheme={getStatusColor(season.status)}>
                              {getStatusLabel(season.status)}
                            </Badge>
                          </Td>
                          <Td py={'20px'}>
                            <Badge colorScheme={season.visible ? 'green' : 'red'}>
                              {season.visible ? 'Visible' : 'Oculta'}
                            </Badge>
                          </Td>
                          <Td py={'20px'}>{season?.startAt && moment(toLocalTimezone(season?.startAt)).format('DD/MM/YYYY')}</Td>
                          <Td py={'20px'}>{season?.endAt && moment(toLocalTimezone(season?.endAt)).format('DD/MM/YYYY')}</Td>
                          <Td py={'20px'}>{season?.totalDays}</Td>
                          <Td py={'20px'}>{season?.challengesCount}</Td>
                          <Td py={'20px'}>{season?.clawMachinePrizesCount}</Td>
                          <Td py={'20px'}>
                            <Actions season={season} onSeasonDeleted={refreshSeasons} onSeasonCloned={refreshSeasons} />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </ChakraTable>
                </TableContainer>
              </Flex>
            </Flex>
          ) : (
            <Box height='300px' display='flex' justifyContent='center' alignItems='center' fontWeight='600' flexDirection='column'>
              <Text>No hay temporadas cargadas</Text>
            </Box>
          )
        )}

        <Pagination
          params={{ limit, page, count }}
          onClick={page =>
            onFilter({
              name: 'page',
              value: String(page + 1)
            })
          }
          onLimitChange={val =>
            onFilter({
              name: 'limit',
              value: val
            })
          }
          hideLimitInput
        />
      </Box>
    </Center>
  )
}

export default SeasonsList;